import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Create axios instance for public web
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('f4c_web_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle auth errors with token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            // Don't refresh for the refresh endpoint itself or terms-of-service
            const requestUrl = originalRequest?.url || '';
            const isRefreshEndpoint = requestUrl.includes('/contacts-otp/refresh');
            const isTermsOfServiceEndpoint = requestUrl.includes('/terms-of-service');

            if (isRefreshEndpoint) {
                // Refresh token itself failed - clear and redirect
                console.log('Refresh token expired - logging out');
                localStorage.removeItem('f4c_web_token');
                localStorage.removeItem('f4c_web_refresh_token');
                localStorage.removeItem('f4c_web_user');
                localStorage.removeItem('f4c_web_token_date');
                window.location.href = '/login';
                return Promise.reject(error);
            }

            if (isTermsOfServiceEndpoint) {
                console.log('401 on terms-of-service endpoint - allowing retry logic to handle');
                return Promise.reject(error);
            }

            // Try to refresh the token
            const refreshToken = localStorage.getItem('f4c_web_refresh_token');
            if (!refreshToken) {
                console.log('No refresh token available - logging out');
                localStorage.removeItem('f4c_web_token');
                localStorage.removeItem('f4c_web_user');
                localStorage.removeItem('f4c_web_token_date');
                window.location.href = '/login';
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Wait for the refresh to complete
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                console.log('Attempting to refresh token...');
                const response = await axios.post(`${API_BASE_URL}/contacts-otp/refresh`, {
                    refresh_token: refreshToken
                });

                if (response.data.success) {
                    const newToken = response.data.access_token;
                    const newRefreshToken = response.data.refresh_token;

                    localStorage.setItem('f4c_web_token', newToken);
                    localStorage.setItem('f4c_web_refresh_token', newRefreshToken);
                    localStorage.setItem('f4c_web_token_date', new Date().toISOString());

                    console.log('Token refreshed successfully');
                    
                    // Update the authorization header
                    api.defaults.headers.Authorization = `Bearer ${newToken}`;
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;

                    processQueue(null, newToken);
                    
                    // Retry the original request
                    return api(originalRequest);
                } else {
                    throw new Error('Refresh failed');
                }
            } catch (refreshError) {
                console.log('Token refresh failed - logging out');
                processQueue(refreshError, null);
                localStorage.removeItem('f4c_web_token');
                localStorage.removeItem('f4c_web_refresh_token');
                localStorage.removeItem('f4c_web_user');
                localStorage.removeItem('f4c_web_token_date');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    // SECURE: Get OTP delivery options for a contact by email (returns masked contact info only)
    getOTPDeliveryOptions: (email) => api.get(`/contacts/email/${encodeURIComponent(email)}/otp-delivery-options`),
    // Generate OTP for a contact
    generateOTP: (contactId, deliveryMethod = 'email', expiresMinutes = 15) =>
        api.post(`/contacts-otp/generate?contact_id=${contactId}&delivery_method=${deliveryMethod}&expires_minutes=${expiresMinutes}`),
    // Generate OTP silently (saves to DB but doesn't send - for testing)
    generateOTPSilent: (contactId, deliveryMethod = 'email', expiresMinutes = 15) =>
        api.post(`/contacts-otp/generate?contact_id=${contactId}&delivery_method=${deliveryMethod}&expires_minutes=${expiresMinutes}&silent=true`),
    // Verify OTP for regular contact access
    verifyOTP: (contactId, code, deliveryMethod) =>
        api.post('/contacts-otp/verify', { contact_id: contactId, code, delivery_method: deliveryMethod }),
    // Get current user information from JWT token
    getCurrentUser: () => api.get('/auth-test/me'),
};

// Contacts API
export const contactsAPI = {
    getById: (id) => api.get(`/contacts/${id}`),
    getMyQRCodeLink: () => api.get('/contacts/me/qr-code-link'),
    getPublicProfile: (id) => api.get(`/contacts/${id}/public-profile`), // Secure endpoint - no sensitive data
    getMyProfile: () => api.get('/contacts/me'), // Authenticated user's full profile
    updateMyProfile: (data) => api.put('/contacts/me', data), // Update authenticated user's profile
    uploadMyImage: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/contacts/me/upload-image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },
    update: (id, data) => api.put(`/contacts/${id}`, data),
};

// Events API - Public endpoints
export const eventsAPI = {
    getByNameId: (nameId) => api.get(`/public/events/${nameId}`),
    getById: (id) => api.get(`/public/events/${id}`),
};

// Event Pages Access API - Get pages user has access to
export const eventPagesAccessAPI = {
    getAccessiblePages: (eventNameId) => api.get(`/public/event-pages-access/${eventNameId}`),
};

// Event Registration API
export const eventRegistrationAPI = {
    create: (data) => api.post('/public/event-registrations', data),
    getByContact: (contactId, eventId) =>
        api.get(`/public/event-registrations/contact/${contactId}/event/${eventId}`),
};

// Unified API service
export const apiService = {
    // Auth
    getOTPDeliveryOptions: (email) => authAPI.getOTPDeliveryOptions(email),
    generateOTP: (contactId, deliveryMethod, expiresMinutes) =>
        authAPI.generateOTP(contactId, deliveryMethod, expiresMinutes),
    verifyOTP: (contactId, code, deliveryMethod) =>
        authAPI.verifyOTP(contactId, code, deliveryMethod),

    // Contacts
    getContact: (id) => contactsAPI.getById(id),
    updateContact: (id, data) => contactsAPI.update(id, data),

    // Events
    getEventByNameId: (nameId) => eventsAPI.getByNameId(nameId),
    getEvent: (id) => eventsAPI.getById(id),

    // Event Pages Access
    getAccessibleEventPages: (eventNameId) => eventPagesAccessAPI.getAccessiblePages(eventNameId),

    // Event Registration
    createEventRegistration: (data) => eventRegistrationAPI.create(data),
    getEventRegistrationByContact: (contactId, eventId) =>
        eventRegistrationAPI.getByContact(contactId, eventId),

    // Attendees / Available Contacts
    getAvailableContacts: (eventId, search = null) => messagingAPI.getAvailableContacts(eventId, search),

    // Hotel Booking
    getUserEventCompanyContact: (eventId) =>
        api.get(`/hotel-booking/user-contact-info?event_id=${eventId}`),
    getHotelBookingLink: (eventCompanyContactId, eventCompanyId) =>
        api.post(`/hotel-booking/link?event_company_contact_id=${eventCompanyContactId}&event_company_id=${eventCompanyId}`),
    setHotelNotNeeded: (eventId, notNeeded) =>
        api.post(`/hotel-booking/hotel-not-needed?event_id=${eventId}&not_needed=${notNeeded}`),
    getEventColleagues: (eventId) =>
        api.get(`/hotel-booking/colleagues?event_id=${eventId}`),
    getColleagueHotelBookingLink: (eventCompanyContactId, eventCompanyId) =>
        api.post(`/hotel-booking/colleague/link?event_company_contact_id=${eventCompanyContactId}&event_company_id=${eventCompanyId}`),

    // Payment Processing
    initiatePayment: (eventCompanyContactId, eventCompanyId) =>
        api.post(`/payments/initiate?event_company_contact_id=${eventCompanyContactId}&event_company_id=${eventCompanyId}`),

    // Company Entities
    getMyCompanyEntitiesWithForms: (eventNameId) =>
        api.get('/company-entities/my/with-forms', { params: eventNameId ? { event_name_id: eventNameId } : {} }),

    // Profile review (F4C 2027+): submit an organization profile for admin approval
    getMyProfileReview: (eventCompanyId) => api.get(`/profile-review/my/${eventCompanyId}`),
    submitProfileForReview: (eventCompanyId) => api.post(`/profile-review/my/${eventCompanyId}/submit`, {}),

    // Donor Circle (F4C 2027+)
    getMyDonorCircle: (eventCompanyId) => api.get(`/donor-circle/my/${eventCompanyId}`),
    saveMyCommitment: (eventCompanyId, data) => api.put(`/donor-circle/my/${eventCompanyId}/commitment`, data),
    saveMyNomination: (eventCompanyId, data) => api.put(`/donor-circle/my/${eventCompanyId}/nomination`, data),

    // Dynamic form answers (partial save of specific answers)
    saveAnswers: (companyEntityId, answers) => api.post(`/dynamic-forms/company-entity/${companyEntityId}/answers`, answers),

    // Document extraction (F4C 2027+)
    getExtractionStatus: () => api.get('/document-extraction/status'),
    listExtractions: (companyEntityId) => api.get(`/document-extraction/company-entity/${companyEntityId}`),
    runExtraction: (companyEntityId, questionId) => api.post(`/document-extraction/company-entity/${companyEntityId}/extract`, null, { params: { question_id: questionId } }),
    applyExtraction: (extractionId, keys) => api.post(`/document-extraction/${extractionId}/apply`, { keys }),

    // Event Entity Companies
    getEventEntityCompaniesWithProfiles: (eventEntityId) =>
        api.get(`/event-entity-companies/${eventEntityId}/profile`),

    // Terms of Service
    getTermsOfServiceContent: () => api.get('/terms-of-service/content'),
    acceptTermsOfService: () => api.post('/terms-of-service/accept'),
    getTermsOfServiceStatus: () => api.get('/terms-of-service/status'),
    checkIcJwtReady: () => api.get('/terms-of-service/ic-ready'),

    // iConnections Messaging
    getICChats: (eventId = null) => api.get('/ic-messaging/chats', { params: eventId ? { event_id: eventId } : {} }),
    getICChatMessages: (chatId) => api.get(`/ic-messaging/chats/${chatId}/messages`),
    sendICMessage: (chatId, content, attachments = []) => api.post(`/ic-messaging/chats/${chatId}/messages`, { content, attachments }),
    markICChatRead: (chatId) => api.post(`/ic-messaging/chats/${chatId}/read`),
    getICUnreadCount: (eventId = null) => api.get('/ic-messaging/unread-count', { params: eventId ? { event_id: eventId } : {} }),
    getContactsForNewChat: (eventId) => api.get(`/ic-messaging/contacts-for-new-chat?event_id=${eventId}`),
    createICChat: (userGuids) => api.post('/ic-messaging/chats', { user_guids: userGuids }),

    // Meeting Request
    getMeetingRequestDetails: (toIcContactId, icEventId) =>
        api.get(`/meeting-request/details?to_ic_contact_id=${toIcContactId}&ic_event_id=${icEventId}`),
    sendMeetingRequest: (data) => api.post('/meeting-request/send', data),

    // Meetings Inbox (iConnections)
    getSentMeetings: (eventName, page = 1, size = 50) =>
        api.post('/meetings-inbox/sent', { event_name: eventName, page, size }),
    getReceivedMeetings: (eventName, page = 1, size = 50) =>
        api.post('/meetings-inbox/received', { event_name: eventName, page, size }),
    getConfirmedMeetings: (eventName) =>
        api.post('/meetings-inbox/confirmed', { event_name: eventName }),
    getAllMeetings: (eventName, eventId = null) =>
        api.post('/meetings-inbox/all', { event_name: eventName, event_id: eventId }),
    getMeetingDetails: (meetingId) =>
        api.get(`/meetings-inbox/details/${meetingId}`),
    cancelMeeting: (meetingId, message = '') =>
        api.post(`/meetings-inbox/cancel/${meetingId}`, { message }),
    acceptMeeting: (meetingId, message = '', meetingTimeId = null) =>
        api.post(`/meetings-inbox/accept/${meetingId}`, { message, meeting_time_id: meetingTimeId }),
    declineMeeting: (meetingId, message = '') =>
        api.post(`/meetings-inbox/decline/${meetingId}`, { message }),
    getMutualAvailability: (senderEventCompanyId, recipientEventCompanyId) =>
        api.post('/meetings-inbox/mutual-availability', {
            sender_event_company_id: senderEventCompanyId,
            recipient_event_company_id: recipientEventCompanyId
        }),
    validateAvailability: (eventId, senderEventCompanyId, recipientEventCompanyId) =>
        api.post('/meetings-inbox/validate-availability', {
            event_id: eventId,
            sender_event_company_id: senderEventCompanyId,
            recipient_event_company_id: recipientEventCompanyId
        }),
    getFileDownloadUrl: (fileUrl) =>
        api.get(`/meetings-inbox/file-download?file_url=${encodeURIComponent(fileUrl)}`),

    // Event Meetings (direct SQL Server query)
    getEventMeetings: (eventId, icEventId) =>
        api.get(`/event-meetings/by-event/${eventId}?ic_event_id=${icEventId}`),
    getPendingMeetingsCount: (eventId, icEventId) =>
        api.get(`/event-meetings/pending-count/${eventId}?ic_event_id=${icEventId}`),
    
    // SQL Direct Meetings - Alternative to iConnections API (same response schema)
    // Use this for potentially better performance - queries SQL Server directly
    // Returns same schema as getAllMeetings so frontend can easily switch
    getAllMeetingsSqlDirect: (eventId, icEventId) =>
        api.get(`/event-meetings/sql-direct/${eventId}?ic_event_id=${icEventId}`),

    // iConnections Agenda
    getEventAgenda: (icEventNameId) =>
        api.get(`/ic-agenda/${icEventNameId}`),

    // Connections (Business Card Exchange)
    getConnections: (eventId, icEventId) =>
        api.get(`/connections/by-event/${eventId}?ic_event_id=${icEventId}`),
};

// Availability API
export const availabilityAPI = {
    getAvailability: (eventNameId) => api.get(`/availability/${eventNameId}`),
    toggleAvailability: (icEventId, meetingTimeId) =>
        api.put(`/availability/${icEventId}/toggle/${meetingTimeId}`),
};

// Messaging API
export const messagingAPI = {
    // Threads
    createThread: (data) => api.post('/messaging/threads', data),
    getThreads: (eventId, skip = 0, limit = 50) =>
        api.get(`/messaging/threads?event_id=${eventId}&skip=${skip}&limit=${limit}`),
    getThread: (threadId) => api.get(`/messaging/threads/${threadId}`),
    getThreadMessages: (threadId, skip = 0, limit = 100, afterMessageId = null) => {
        let url = `/messaging/threads/${threadId}/messages?skip=${skip}&limit=${limit}`;
        if (afterMessageId) url += `&after_message_id=${afterMessageId}`;
        return api.get(url);
    },

    // Messages
    sendMessage: (threadId, content) =>
        api.post(`/messaging/threads/${threadId}/messages`, { content }),

    // Participants
    addParticipants: (threadId, contactIds) =>
        api.post(`/messaging/threads/${threadId}/participants`, { contact_ids: contactIds }),
    getAddableParticipants: (threadId, search = null) => {
        let url = `/messaging/threads/${threadId}/addable-participants`;
        if (search) url += `?search=${encodeURIComponent(search)}`;
        return api.get(url);
    },
    leaveThread: (threadId) => api.delete(`/messaging/threads/${threadId}/leave`),

    // Read Status
    markThreadRead: (threadId) => api.post(`/messaging/threads/${threadId}/read`),
    getUnreadCount: (eventId) => api.get(`/messaging/unread-count?event_id=${eventId}`),

    // Available Contacts
    getAvailableContacts: (eventId, search = null) => {
        let url = `/messaging/available-contacts?event_id=${eventId}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return api.get(url);
    },

    // Polling
    poll: (eventId, lastCheckTimestamp = null) => {
        let url = `/messaging/poll?event_id=${eventId}`;
        if (lastCheckTimestamp) url += `&last_check_timestamp=${encodeURIComponent(lastCheckTimestamp)}`;
        return api.get(url);
    },
};

// Notification Settings API
export const notificationSettingsAPI = {
    getSettings: () => api.get('/notification-settings'),
    toggleSetting: (data) => api.post('/notification-settings/toggle', data),
};

export default api;

